import { createPortal } from 'react-dom'
import { useShellOptional } from './ShellContext'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import type { ChatMessage } from '@mezha/api'
import { useChatQuery, useSendMutation, type Channel } from '../api/endpoints/social'
import { useHeroQuery } from '../api/endpoints/hero'
import { useMeQuery } from '../api/endpoints/auth'
import { fromFailure, type ApiFailure } from '../api/baseApi'
import { useAppDispatch, useAppSelector } from '../app/store'
import { ui } from '../app/uiSlice'
import { Emblem } from '../ui/brand/Emblems'
import { cx } from '../ui'
import { explain } from '../lib/explain'

type Tab = { id: Channel; label: string }

const ALL: Tab[] = [
  { id: 'place', label: 'Площадь' },
  { id: 'private', label: 'Личное' },
  { id: 'clan', label: 'Клан' },
  { id: 'system', label: 'События' },
]

const who = (m: ChatMessage) => m.sender_name || 'Межа'

/** Chat slot: collapsed line → open panel with channels of the place, private
 *  messages, the clan and system events. Messages arrive as `chat.message` events. */
export function ChatSlot({ compact }: { compact?: boolean }) {
  const combatHost = useShellOptional()?.slot('combat-chat')
  const dispatch = useAppDispatch()
  const { data: hero } = useHeroQuery()
  const { data: me } = useMeQuery()
  const unread = useAppSelector(s => s.ui.unread)
  const last = useAppSelector(s => s.ui.lastMessage)
  const dm = useAppSelector(s => s.ui.dm)
  const tabs = ALL
  const noClan = hero?.clan_id == null
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Channel>('place')
  const [text, setText] = useState('')
  const [sendError, setSendError] = useState<string | null>(null)
  const [send, { isLoading: sending }] = useSendMutation()
  const page = useChatQuery(tab, { skip: !open })
  const logRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const lineRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    dispatch(ui.actions.chatViewing(open ? tab : null))
  }, [dispatch, open, tab])
  useEffect(() => {
    if (dm) {
      setTab('private')
      setOpen(true)
    }
  }, [dm])

  const shown = useMemo(() => {
    const list = [...(page.data?.messages ?? [])].reverse()
    if (tab === 'private' && dm) return list.filter(m => m.sender_id === dm.id || m.receiver_id === dm.id)
    return list
  }, [page.data, tab, dm])
  useEffect(() => {
    if (open && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [open, shown.length])

  const total = unread.place + unread.private + unread.clan + unread.system
  const canWrite = tab === 'place' || tab === 'clan' || (tab === 'private' && dm != null)
  const muted = me?.muted === true

  const submit = async () => {
    const t = text.trim()
    if (!t || sending || !canWrite) return
    setSendError(null)
    try {
      await send(tab === 'private' && dm ? { channel: 'private', text: t, to: dm.id } : { channel: tab, text: t }).unwrap()
      setText('')
    } catch (e) {
      // The text stays in the field so nothing typed is lost.
      setSendError(explain(fromFailure(e as ApiFailure)))
    } finally {
      inputRef.current?.focus()
    }
  }

  const collapse = () => {
    setOpen(false)
    requestAnimationFrame(() => lineRef.current?.focus())
  }
  const onPanelKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      collapse()
    }
  }
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!d) return
    e.preventDefault()
    let n = (index + d + tabs.length) % tabs.length
    if (tabs[n].id === 'clan' && noClan) n = (n + d + tabs.length) % tabs.length
    const next = tabs[n].id
    setTab(next)
    document.getElementById(`chat-tab-${next}`)?.focus()
  }

  if (!open) {
    const line = (
      <div className={cx('chat chat--collapsed', total > 0 && 'chat--new', compact && 'chat--combat')}>
        <button type="button" ref={lineRef} className="chat__line slab" onClick={() => setOpen(true)} aria-expanded="false" aria-controls="shell-chat"
          aria-label={total ? `Открыть чат, новых сообщений: ${total}` : 'Открыть чат'}>
          <Emblem name="loc.talk" size={20} />
          <span className="last">{last ? <><b>{last.channel === 'system' ? 'События' : who(last)}:</b> {last.text}</> : <i>Чат площади</i>}</span>
          {total > 0 && <span className="gem" aria-hidden="true">{total > 99 ? '99+' : total}</span>}
        </button>
      </div>
    )
    return compact && combatHost ? createPortal(line, combatHost) : line
  }

  const placeholder = muted ? 'Вам запрещено писать в чат'
    : tab === 'private' ? (dm ? `Лично для ${dm.name}…` : 'Выберите собеседника: нажмите имя на площади')
      : tab === 'clan' ? 'Сказать клану…' : 'Сказать здесь…'

  return (
    <div className="chat is-open" id="shell-chat" role="region" aria-label="Чат" onKeyDown={onPanelKey}>
      <div className="chat__tabs" role="tablist" aria-label="Каналы чата">
        {tabs.map((t, i) => (
          <button key={t.id} type="button" role="tab" id={`chat-tab-${t.id}`} aria-selected={tab === t.id} aria-controls="chat-log" tabIndex={tab === t.id ? 0 : -1}
            className={cx('chat__tab', tab === t.id && 'is-active')} onClick={() => setTab(t.id)} onKeyDown={e => onTabKey(e, i)}
            disabled={t.id === 'clan' && noClan} title={t.id === 'clan' && noClan ? 'Вы не в клане' : undefined}>
            {t.label}{unread[t.id] > 0 && tab !== t.id && <span className="gem" aria-label={`новых: ${unread[t.id]}`}>{unread[t.id]}</span>}
          </button>
        ))}
        <button type="button" className="chat__close" onClick={collapse} aria-label="Свернуть чат"><Emblem name="nav.more" size={16} /></button>
      </div>
      <div className="chat__log" id="chat-log" role="tabpanel" aria-labelledby={`chat-tab-${tab}`} ref={logRef} tabIndex={0}>
        {tab === 'private' && dm && (
          <p className="chat__msg is-system">Разговор с <b>{dm.name}</b> <button type="button" className="linkbtn" onClick={() => dispatch(ui.actions.openDm(null))}>все личные</button></p>
        )}
        {page.isError && <p className="chat__msg is-system" role="alert">История недоступна <button type="button" className="linkbtn" onClick={() => void page.refetch()}>Повторить</button></p>}
        {page.isSuccess && shown.length === 0 && (
          <p className="chat__msg is-system">{tab === 'system' ? 'Событий пока нет.' : tab === 'private' ? 'Личных сообщений нет.' : 'Пока тихо.'}</p>
        )}
        {shown.map(m => (
          <p key={m.id} className={cx('chat__msg', m.channel === 'private' && 'is-private', m.channel === 'system' && 'is-system')}>
            {m.channel !== 'system' && (
              m.sender_id != null && m.sender_id !== me?.account_id
                ? <button type="button" className="chat__who" onClick={() => dispatch(ui.actions.openDm({ id: m.sender_id as number, name: who(m) }))} aria-label={`Написать лично: ${who(m)}`}>{who(m)}:</button>
                : <span>{who(m)}:</span>
            )} {m.text}
          </p>
        ))}
      </div>
      {tab !== 'system' && (
        <form className="chat__input slab" onSubmit={e => { e.preventDefault(); void submit() }}>
          <input ref={inputRef} value={text} onChange={e => { setText(e.target.value); if (sendError) setSendError(null) }} disabled={!canWrite || muted}
            placeholder={placeholder} aria-label={placeholder} aria-describedby={sendError ? 'chat-send-error' : undefined} maxLength={500} />
          <small className="chat__left" aria-live="off">{500 - text.length}</small>
          <button type="submit" className="chat__send" disabled={!canWrite || muted || !text.trim() || sending} aria-label="Отправить"><Emblem name="map.travel" size={16} /></button>
        </form>
      )}
      {sendError && <p className="chat__error" id="chat-send-error" role="alert">{sendError}</p>}
    </div>
  )
}
